import { apiRequest } from '../api';
import { state, saveSession } from '../state';
import { t, getLocale } from '../i18n';
import { navigateTo } from '../router';
import type { PlayerWithStats, PlayersListResponse, TitleSize } from '../types';
import { TITLE_SIZES } from '../types';
import {
    listRefs,
    createRef,
    updateRef,
    deleteRef,
    uploadMapImage,
    deleteMapImage,
    type RefType,
    type RefItem,
    type GameFormat,
    type GameHost,
    type GameMap,
    type GameMod,
    MAP_TYPES,
    TEAM_FORMATS,
} from '../api/game-refs';
import { teamFormatLabel } from '../ui/map-presets';
import { TITLE_COLORS } from '../titles';
import { attachAutocomplete } from '../ui/autocomplete';
import { getMapAuthors, setMapAuthors } from '../ui/map-authors';
import { getModAuthors, setModAuthors } from '../ui/mod-authors';

type Role = 'moderator' | 'admin' | 'ghost';

interface AdminUser {
    id: string;
    email: string;
    username: string | null;
    created_at: string | null;
    last_seen_at: string | null;
    roles: Role[];
    player_name: string | null;
    can_rate: boolean;
    banned_at: string | null;
}

interface AdminUsersResponse {
    users: AdminUser[];
}

interface AdminArchivedRating {
    id: number;
    original_id: number;
    player_id: number;
    user_id: string;
    player_name: string | null;
    user_name: string | null;
    race: string;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
    deleted_at: string;
}

interface AdminArchiveResponse {
    archived: AdminArchivedRating[];
    total: number;
}

/** Запись корзины удалённых игр и модов. */
interface AdminDeletedItem {
    id: number;
    entity_type: 'game' | 'mod';
    entity_id: number;
    title: string;
    deleted_by_name: string | null;
    deleted_at: string;
}

interface AdminRating {
    id: number;
    user_id: string;
    email: string;
    username: string | null;
    race: string;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
    created_at: string;
    updated_at: string;
}

interface AdminLog {
    id: number;
    created_at: string;
    actor_id: string | null;
    actor_username: string | null;
    action: string;
    entity_type: string | null;
    entity_id: number | null;
    summary: string;
    details: Record<string, unknown>;
}

interface AdminLogsResponse {
    logs: AdminLog[];
    total: number;
}

interface AdminVisit {
    id: number;
    created_at: string;
    path: string;
    referrer: string | null;
    user_agent: string | null;
    ip: string | null;
    visitor_id: string | null;
    user_id: string | null;
    username: string | null;
}

interface AdminVisitsResponse {
    visits: AdminVisit[];
    total: number;
}

interface AdminVisitDay {
    day: string;      // YYYY-MM-DD (Europe/Moscow)
    views: number;
    visitors: number;
    users: number;
}

interface AdminVisitStats {
    from: string;
    to: string;
    summary: { views: number; visitors: number; registered: number };
    daily: AdminVisitDay[];
    top_pages: { path: string; views: number; visitors: number }[];
}

interface AdminTitle {
    id: number;
    name: string;
    name_en?: string | null;
    color: string;
    size?: TitleSize;
    player_ids: number[];
}

interface AdminStreamer {
    id: number;
    name: string;
    url: string;
    sort_order: number | null;
    test_live: boolean;
    player_id: number | null;
    player_name?: string | null;
}

interface AdminDonation {
    id: number;
    donor_name: string;
    amount: number;
    currency: string;
    message: string | null;
    donated_at: string;
}

const LOG_ACTIONS: { value: string; key: string }[] = [
    { value: 'auth.signup', key: 'log.action.signup' },
    { value: 'auth.login', key: 'log.action.login' },
    { value: 'auth.logout', key: 'log.action.logout' },
    { value: 'player.create', key: 'log.action.player_create' },
    { value: 'player.rename', key: 'log.action.player_rename' },
    { value: 'player.aka', key: 'log.action.player_aka' },
    { value: 'player.delete', key: 'log.action.player_delete' },
    { value: 'player.link', key: 'log.action.player_link' },
    { value: 'player.unlink', key: 'log.action.player_unlink' },
    { value: 'rating.create', key: 'log.action.rating_create' },
    { value: 'rating.update', key: 'log.action.rating_update' },
    { value: 'rating.delete', key: 'log.action.rating_delete' },
    { value: 'game.create', key: 'log.action.game_create' },
    { value: 'game.update', key: 'log.action.game_update' },
    { value: 'game.delete', key: 'log.action.game_delete' },
    { value: 'ref.create', key: 'log.action.ref_create' },
    { value: 'ref.update', key: 'log.action.ref_update' },
    { value: 'ref.delete', key: 'log.action.ref_delete' },
    { value: 'streamer.create', key: 'log.action.streamer_create' },
    { value: 'streamer.update', key: 'log.action.streamer_update' },
    { value: 'streamer.delete', key: 'log.action.streamer_delete' },
    { value: 'donation.create', key: 'log.action.donation_create' },
    { value: 'donation.update', key: 'log.action.donation_update' },
    { value: 'donation.delete', key: 'log.action.donation_delete' },
    { value: 'role.grant', key: 'log.action.role_grant' },
    { value: 'role.revoke', key: 'log.action.role_revoke' },
    { value: 'user.delete', key: 'log.action.user_delete' },
    { value: 'user.rate_allow', key: 'log.action.user_rate_allow' },
    { value: 'user.rate_block', key: 'log.action.user_rate_block' },
    { value: 'user.ban', key: 'log.action.user_ban' },
    { value: 'user.unban', key: 'log.action.user_unban' },
    { value: 'auth.forgot_password', key: 'log.action.auth_forgot_password' },
    { value: 'auth.reset_password', key: 'log.action.auth_reset_password' },
    { value: 'rating.restore', key: 'log.action.rating_restore' },
    { value: 'preset.create', key: 'log.action.preset_create' },
    { value: 'preset.update', key: 'log.action.preset_update' },
    { value: 'preset.delete', key: 'log.action.preset_delete' },
    { value: 'user.impersonate', key: 'log.action.user_impersonate' },
    { value: 'user.set_password', key: 'log.action.user_set_password' },
    { value: 'user.update_username', key: 'log.action.user_update_username' },
    { value: 'user.update_email', key: 'log.action.user_update_email' },
    { value: 'user.update_password', key: 'log.action.user_update_password' },
    { value: 'title.create', key: 'log.action.title_create' },
    { value: 'title.update', key: 'log.action.title_update' },
    { value: 'title.delete', key: 'log.action.title_delete' },
    { value: 'title.assign', key: 'log.action.title_assign' },
    { value: 'title.unassign', key: 'log.action.title_unassign' },
    { value: 'reaction.player', key: 'log.action.reaction_player' },
    { value: 'reaction.game', key: 'log.action.reaction_game' },
    { value: 'reaction.mvp', key: 'log.action.reaction_mvp' },
    { value: 'reaction.map', key: 'log.action.reaction_map' },
    { value: 'reaction.mod', key: 'log.action.reaction_mod' },
    { value: 'follow.add', key: 'log.action.follow_add' },
    { value: 'follow.remove', key: 'log.action.follow_remove' },
    { value: 'share.card', key: 'log.action.share_card' },
];

const LOG_ACTION_LABELS: Record<string, string> = {};
for (const a of LOG_ACTIONS) LOG_ACTION_LABELS[a.value] = a.key;

interface RefConfig {
    type: RefType;
    containerId: string;
    buttonId: string;
    titleKey: string;
    newPromptKey: string;
    newErrorKey: string;
    deleteConfirmKey: string;
    editableFields: ('slug' | 'elo_weight' | 'aka' | 'alt_name' | 'map_types' | 'team_formats' | 'player_count' | 'author' | 'link' | 'player_id' | 'description')[];
}

const REF_CONFIGS: RefConfig[] = [
    {
        type: 'formats',
        containerId: 'admin-formats',
        buttonId: 'btn-add-formats',
        titleKey: 'admin.formats_title',
        newPromptKey: 'admin.new_format_prompt',
        newErrorKey: 'admin.new_format_error',
        deleteConfirmKey: 'admin.delete_format_confirm',
        editableFields: ['slug', 'elo_weight'],
    },
    {
        type: 'hosts',
        containerId: 'admin-hosts',
        buttonId: 'btn-add-hosts',
        titleKey: 'admin.hosts_title',
        newPromptKey: 'admin.new_host_prompt',
        newErrorKey: 'admin.new_host_error',
        deleteConfirmKey: 'admin.delete_host_confirm',
        editableFields: ['aka', 'player_id'],
    },
    {
        type: 'maps',
        containerId: 'admin-maps',
        buttonId: 'btn-add-maps',
        titleKey: 'admin.maps_title',
        newPromptKey: 'admin.new_map_prompt',
        newErrorKey: 'admin.new_map_error',
        deleteConfirmKey: 'admin.delete_map_confirm',
        editableFields: ['alt_name', 'player_count', 'map_types', 'team_formats', 'author', 'link'],
    },
    {
        type: 'mods',
        containerId: 'admin-mods',
        buttonId: 'btn-add-mods',
        titleKey: 'admin.mods_title',
        newPromptKey: 'admin.new_mod_prompt',
        newErrorKey: 'admin.new_mod_error',
        deleteConfirmKey: 'admin.delete_mod_confirm',
        editableFields: ['author', 'description'],
    },
];

let abortController: AbortController | null = null;
let cachedPlayers: PlayerWithStats[] = [];
let usersBox: HTMLElement | null = null;
let playersBox: HTMLElement | null = null;
let searchInput: HTMLInputElement | null = null;
let playersSearchInput: HTMLInputElement | null = null;
let logsBox: HTMLElement | null = null;
let logsSearchInput: HTMLInputElement | null = null;
let logsActionSelect: HTMLSelectElement | null = null;
let visitsStatsBox: HTMLElement | null = null;
let visitsLogBox: HTMLElement | null = null;
let visitsDaysSelect: HTMLSelectElement | null = null;
let visitsSearchInput: HTMLInputElement | null = null;
let archiveBox: HTMLElement | null = null;
let archiveSearchInput: HTMLInputElement | null = null;
let titlesBox: HTMLElement | null = null;
let streamersBox: HTMLElement | null = null;
let donationsBox: HTMLElement | null = null;
const refSearchInputs: Partial<Record<RefType, HTMLInputElement>> = {};

const refsCache: Record<RefType, RefItem[]> = {
    formats: [],
    hosts: [],
    maps: [],
    mods: [],
};

// ============================================================
// Mount / unmount
// ============================================================
export function mountAdmin(_params: URLSearchParams): void {
    if (!state.token || !state.user) {
        sessionStorage.setItem('redirectAfterLogin', '/admin');
        navigateTo('/', true);
        return;
    }
    if (!state.user.is_admin) {
        navigateTo('/', true);
        return;
    }

    const screen = document.getElementById('screen-admin');
    if (screen) screen.classList.remove('hidden');

    abortController = new AbortController();
    const { signal } = abortController;

    usersBox = document.getElementById('admin-users');
    playersBox = document.getElementById('admin-players');
    searchInput = document.getElementById('admin-search') as HTMLInputElement | null;
    playersSearchInput = document.getElementById('admin-players-search') as HTMLInputElement | null;
    logsBox = document.getElementById('admin-logs');
    logsSearchInput = document.getElementById('admin-logs-search') as HTMLInputElement | null;
    logsActionSelect = document.getElementById('admin-logs-action') as HTMLSelectElement | null;
    visitsStatsBox = document.getElementById('admin-visits-stats');
    visitsLogBox = document.getElementById('admin-visits-log');
    visitsDaysSelect = document.getElementById('admin-visits-days') as HTMLSelectElement | null;
    visitsSearchInput = document.getElementById('admin-visits-search') as HTMLInputElement | null;
    archiveBox = document.getElementById('admin-ratings-archive');
    archiveSearchInput = document.getElementById('admin-archive-search') as HTMLInputElement | null;
    titlesBox = document.getElementById('admin-titles');
    streamersBox = document.getElementById('admin-streamers');
    donationsBox = document.getElementById('admin-donations');

    searchInput?.addEventListener('input', () => {
        void loadUsers(searchInput!.value);
    }, { signal });

    playersSearchInput?.addEventListener('input', () => {
        renderPlayersFiltered();
    }, { signal });

    populateLogActionOptions();
    logsSearchInput?.addEventListener('input', () => {
        void loadLogs();
    }, { signal });
    logsActionSelect?.addEventListener('change', () => {
        void loadLogs();
    }, { signal });
    document.getElementById('btn-admin-logs-refresh')?.addEventListener('click', () => {
        void loadLogs(true);
    }, { signal });
    visitsDaysSelect?.addEventListener('change', () => {
        void loadVisitStats(true);
    }, { signal });
    visitsSearchInput?.addEventListener('input', () => {
        void loadVisits();
    }, { signal });
    document.getElementById('btn-admin-visits-stats-refresh')?.addEventListener('click', () => {
        void loadVisitStats(true);
    }, { signal });
    document.getElementById('btn-admin-visits-refresh')?.addEventListener('click', () => {
        void loadVisits(true);
    }, { signal });
    archiveSearchInput?.addEventListener('input', () => {
        void loadArchive();
    }, { signal });
    document.getElementById('btn-admin-archive-refresh')?.addEventListener('click', () => {
        void loadArchive();
    }, { signal });
    document.getElementById('btn-admin-trash-refresh')?.addEventListener('click', () => {
        void loadTrash();
    }, { signal });

    // Справочники: кнопки «+ Добавить»
    for (const config of REF_CONFIGS) {
        const btn = document.getElementById(config.buttonId);
        btn?.addEventListener('click', () => {
            void addRefInline(config);
        }, { signal });
    }

    // Справочники: поиск по названию (карты — ещё и по альт. названию)
    for (const config of REF_CONFIGS) {
        const input = document.getElementById(
            `admin-${config.type}-search`
        ) as HTMLInputElement | null;
        if (!input) continue;
        refSearchInputs[config.type] = input;
        input.addEventListener('input', () => renderRefs(config), { signal });
    }

    // Титулы
    document.getElementById('btn-add-title')?.addEventListener('click', () => {
        void createTitle();
    }, { signal });

    // Стримеры
    document.getElementById('btn-add-streamer')?.addEventListener('click', () => {
        void addStreamer();
    }, { signal });

    // Донаты: добавляются кнопкой из формы, список правится автосохранением.
    const donationDate = document.getElementById('donation-date') as HTMLInputElement | null;
    if (donationDate && !donationDate.value) {
        donationDate.value = new Date().toISOString().slice(0, 10);
    }
    document.getElementById('btn-add-donation')?.addEventListener('click', () => {
        void addDonation();
    }, { signal });

    void loadUsers();
    void loadLogs();
    void loadVisitStats();
    void loadVisits();
    void loadArchive();
    void loadTrash();
    void loadStreamers();
    void loadDonations();

    // Игроки грузим раньше справочников, чтобы в карточках ведущих был список игроков.
    void loadPlayers().then(() => {
        for (const config of REF_CONFIGS) {
            void loadRefs(config);
        }
        void loadTitles();
    });
}

export function unmountAdmin(): void {
    abortController?.abort();
    abortController = null;
    // Сбрасываем отложенные автocохранения титулов, чтобы не терять правки
    for (const [id, timer] of titleDebounce) {
        clearTimeout(timer);
        void saveTitle(id);
    }
    titleDebounce.clear();
    for (const [id, timer] of streamerDebounce) {
        clearTimeout(timer);
        void saveStreamer(id);
    }
    streamerDebounce.clear();
    for (const [id, timer] of donationDebounce) {
        clearTimeout(timer);
        void saveDonation(id);
    }
    donationDebounce.clear();
}

// ============================================================
// Пользователи
// ============================================================
async function loadUsers(query = ''): Promise<void> {
    if (!usersBox) return;
    usersBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const url = query
            ? `/api/admin/users?q=${encodeURIComponent(query)}`
            : '/api/admin/users';
        const res = await apiRequest<AdminUsersResponse>(url, { token: state.token });
        renderUsers(res.users);
    } catch (err) {
        usersBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderUsers(users: AdminUser[]): void {
    if (!usersBox) return;

    if (users.length === 0) {
        usersBox.innerHTML = `<p class="hint">${t('admin.no_users')}</p>`;
        return;
    }

    usersBox.innerHTML = '';
    for (const u of users) {
        usersBox.appendChild(buildUserCard(u));
    }
}

function roleBadgeLabel(role: Role): string {
    if (role === 'admin') return `★ ${t('topbar.role_admin')}`;
    if (role === 'ghost') return `💠 ${t('topbar.role_ghost')}`;
    return `◆ ${t('topbar.role_moderator')}`;
}

function buildUserCard(u: AdminUser): HTMLElement {
    const card = document.createElement('div');
    card.className = 'admin-user-card' + (u.banned_at ? ' is-banned' : '');

    const info = document.createElement('div');
    info.className = 'admin-user-info';

    const email = document.createElement('div');
    email.className = 'admin-user-email';
    email.textContent = u.email;

    const username = document.createElement('div');
    username.className = 'admin-user-username';
    username.textContent = u.username ?? t('admin.user_no_username');

    info.append(email, username);

    if (u.banned_at) {
        const bannedBadge = document.createElement('span');
        bannedBadge.className = 'role-badge role-badge--banned';
        bannedBadge.textContent = t('admin.user_banned');
        info.appendChild(bannedBadge);
    }

    if (u.player_name) {
        const linkedPlayer = document.createElement('div');
        linkedPlayer.className = 'admin-user-player';
        linkedPlayer.textContent = `🔗 ${t('admin.user_linked_player', { name: u.player_name })}`;
        info.appendChild(linkedPlayer);
    }

    if (u.created_at) {
        const reg = document.createElement('div');
        reg.className = 'admin-user-registered';
        const d = new Date(u.created_at);
        const formatted = d.toLocaleDateString(
            getLocale() === 'ru' ? 'ru-RU' : 'en-US',
            { day: '2-digit', month: '2-digit', year: 'numeric' }
        );
        reg.textContent = `${t('admin.registered_at')}: ${formatted}`;
        info.appendChild(reg);
    }

    if (u.last_seen_at) {
        const lastSeen = document.createElement('div');
        lastSeen.className = 'admin-user-lastseen';
        const d = new Date(u.last_seen_at);
        const formatted = d.toLocaleDateString(
            getLocale() === 'ru' ? 'ru-RU' : 'en-US',
            { day: '2-digit', month: '2-digit', year: 'numeric' }
        );
        lastSeen.textContent = `${t('admin.last_seen')}: ${formatted} · ${relativeTime(u.last_seen_at)}`;
        lastSeen.title = d.toLocaleString();
        info.appendChild(lastSeen);
    }

    if (u.roles.length > 0) {
        const rolesWrap = document.createElement('div');
        rolesWrap.className = 'admin-user-roles';
        for (const r of u.roles) {
            const badge = document.createElement('span');
            badge.className = `role-badge role-badge--${r}`;
            badge.textContent = roleBadgeLabel(r);
            rolesWrap.appendChild(badge);
        }
        info.appendChild(rolesWrap);
    }

    card.appendChild(info);

    const actions = document.createElement('div');
    actions.className = 'admin-user-actions';

    const isMod = u.roles.includes('moderator');
    const isAdmin = u.roles.includes('admin');

    const modBtn = document.createElement('button');
    modBtn.type = 'button';
    modBtn.className = isMod ? 'admin-role-btn is-active' : 'admin-role-btn';
    modBtn.textContent = isMod ? `◆ ${t('admin.revoke_mod')}` : `◆ ${t('admin.grant_mod')}`;
    modBtn.addEventListener('click', () => void toggleRole(u, 'moderator', isMod));
    actions.appendChild(modBtn);

    const adminBtn = document.createElement('button');
    adminBtn.type = 'button';
    adminBtn.className = isAdmin
        ? 'admin-role-btn admin-role-btn--admin is-active'
        : 'admin-role-btn admin-role-btn--admin';
    adminBtn.textContent = isAdmin ? `★ ${t('admin.revoke_admin')}` : `★ ${t('admin.grant_admin')}`;
    adminBtn.addEventListener('click', () => void toggleRole(u, 'admin', isAdmin));
    actions.appendChild(adminBtn);

    const isGhost = u.roles.includes('ghost');
    const ghostBtn = document.createElement('button');
    ghostBtn.type = 'button';
    ghostBtn.className = isGhost
        ? 'admin-role-btn admin-role-btn--ghost is-active'
        : 'admin-role-btn admin-role-btn--ghost';
    ghostBtn.textContent = isGhost ? `💠 ${t('admin.revoke_ghost')}` : `💠 ${t('admin.grant_ghost')}`;
    ghostBtn.addEventListener('click', () => void toggleRole(u, 'ghost', isGhost));
    actions.appendChild(ghostBtn);

    const rateBtn = document.createElement('button');
    rateBtn.type = 'button';
    rateBtn.className = u.can_rate
        ? 'admin-role-btn admin-role-btn--rate'
        : 'admin-role-btn admin-role-btn--rate is-active';
    rateBtn.textContent = u.can_rate
        ? `🚫 ${t('admin.disable_rate')}`
        : `✓ ${t('admin.enable_rate')}`;
    rateBtn.title = u.can_rate
        ? t('admin.disable_rate_title')
        : t('admin.enable_rate_title');
    rateBtn.addEventListener('click', () => void toggleCanRate(u));
    actions.appendChild(rateBtn);

    const banBtn = document.createElement('button');
    banBtn.type = 'button';
    banBtn.className = u.banned_at
        ? 'admin-role-btn admin-role-btn--ban is-active'
        : 'admin-role-btn admin-role-btn--ban';
    banBtn.textContent = u.banned_at
        ? `🔓 ${t('admin.unban_user')}`
        : `🔨 ${t('admin.ban_user')}`;
    banBtn.title = u.banned_at
        ? t('admin.unban_title')
        : t('admin.ban_title');
    banBtn.addEventListener('click', () => void toggleBan(u));
    actions.appendChild(banBtn);

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'admin-user-delete-btn';
    delBtn.textContent = '🗑';
    delBtn.title = t('admin.delete_user');
    delBtn.addEventListener('click', () => void deleteUser(u));
    actions.appendChild(delBtn);

    const impBtn = document.createElement('button');
    impBtn.type = 'button';
    impBtn.className = 'admin-role-btn admin-role-btn--impersonate';
    impBtn.textContent = `↬ ${t('admin.impersonate')}`;
    impBtn.title = t('admin.impersonate_title');
    impBtn.addEventListener('click', () => void impersonateUser(u));
    actions.appendChild(impBtn);

    // Админ-просмотр личной статистики игрока, привязанного к аккаунту.
    if (u.player_name) {
        const statsBtn = document.createElement('button');
        statsBtn.type = 'button';
        statsBtn.className = 'admin-role-btn';
        statsBtn.textContent = `📊 ${t('admin.view_stats')}`;
        statsBtn.title = t('admin.view_stats_title');
        statsBtn.addEventListener('click', () => {
            navigateTo(`/my-stats?user_id=${encodeURIComponent(u.id)}`);
        });
        actions.appendChild(statsBtn);
    }

    // Сброс пароля: старый пароль в Auth хэширован и недоступен, поэтому
    // админ может только задать новый (временный) и увидеть его один раз.
    const pwdBtn = document.createElement('button');
    pwdBtn.type = 'button';
    pwdBtn.className = 'admin-role-btn admin-role-btn--password';
    pwdBtn.textContent = `🔑 ${t('admin.set_password')}`;
    pwdBtn.title = t('admin.set_password_title');
    pwdBtn.addEventListener('click', () => void setUserPassword(u, card));
    actions.appendChild(pwdBtn);

    card.appendChild(actions);
    return card;
}

/**
 * Сброс пароля пользователя: вызываем сервер, получаем новый пароль и
 * показываем его в карточке СКРЫТЫМ (input type=password) — виден только
 * по кнопке-глазу. Повторно посмотреть пароль невозможно (в Auth он хэширован).
 */
async function setUserPassword(u: AdminUser, card: HTMLElement): Promise<void> {
    const label = u.username || u.email;
    if (!confirm(t('admin.set_password_confirm', { user: label }))) return;

    try {
        const res = await apiRequest<{ password: string; generated: boolean }>(
            `/api/admin/users/${u.id}/set-password`,
            { method: 'POST', token: state.token }
        );

        // Убираем предыдущую плашку, если админ сбрасывал пароль повторно
        card.querySelector('.admin-password-row')?.remove();

        const row = document.createElement('div');
        row.className = 'admin-password-row';

        const input = document.createElement('input');
        input.type = 'password';
        input.readOnly = true;
        input.value = res.password;
        input.className = 'admin-password-input';
        row.appendChild(input);

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'admin-role-btn';
        toggle.textContent = '👁';
        toggle.title = t('admin.password_show');
        let shown = false;
        toggle.addEventListener('click', () => {
            shown = !shown;
            input.type = shown ? 'text' : 'password';
            toggle.title = shown ? t('admin.password_hide') : t('admin.password_show');
        });
        row.appendChild(toggle);

        const copy = document.createElement('button');
        copy.type = 'button';
        copy.className = 'admin-role-btn';
        copy.textContent = '📋';
        copy.title = t('admin.password_copy');
        copy.addEventListener('click', () => {
            void navigator.clipboard?.writeText(res.password);
        });
        row.appendChild(copy);

        const hint = document.createElement('span');
        hint.className = 'admin-password-hint';
        hint.textContent = t('admin.password_new_hint');
        row.appendChild(hint);

        card.appendChild(row);
    } catch (err) {
        alert(t('admin.set_password_error') + (err instanceof Error ? err.message : String(err)));
    }
}

/**
 * Имперсонация: выпускаем токен целевого пользователя на сервере,
 * сохраняем его сессию и догружаем роли через /me, затем уходим домой.
 */
async function impersonateUser(u: AdminUser): Promise<void> {
    const label = u.username || u.email;
    if (!confirm(t('admin.impersonate_confirm', { user: label }))) return;

    try {
        const res = await apiRequest<{ access_token: string; refresh_token: string }>(
            `/api/admin/users/${u.id}/impersonate`,
            { method: 'POST', token: state.token }
        );

        // Догружаем роли/username через /me новым токеном
        const me = await apiRequest<{
            user: {
                id: string; email: string; username: string | null;
                is_moderator: boolean; is_admin: boolean; is_ghost: boolean;
                can_rate: boolean; player_id: number | null; player_name: string | null;
            };
        }>('/api/auth/me', { token: res.access_token });

        saveSession(
            {
                id: me.user.id,
                email: me.user.email,
                username: me.user.username,
                is_moderator: me.user.is_moderator,
                is_admin: me.user.is_admin,
                is_ghost: me.user.is_ghost,
                can_rate: me.user.can_rate,
                player_id: me.user.player_id ?? null,
                player_name: me.user.player_name ?? null,
            },
            res.access_token,
            res.refresh_token
        );

        navigateTo('/', true);
    } catch (err) {
        alert(t('admin.impersonate_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function toggleRole(user: AdminUser, role: Role, currentlyHas: boolean): Promise<void> {
    const action = currentlyHas ? 'revoke' : 'grant';
    const roleName = role === 'admin' ? 'ADMIN' : role === 'ghost' ? 'GHOST' : 'MOD';

    if (!confirm(t('admin.role_toggle_confirm', { role: roleName, email: user.email }))) return;

    try {
        await apiRequest(`/api/admin/roles/${action}`, {
            method: 'POST',
            token: state.token,
            body: { userId: user.id, role },
        });
        await loadUsers(searchInput?.value ?? '');
    } catch (err) {
        alert(t('admin.role_toggle_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function toggleCanRate(user: AdminUser): Promise<void> {
    const next = !user.can_rate;
    const label = user.username || user.email;
    const confirmText = next
        ? t('admin.enable_rate_confirm', { user: label })
        : t('admin.disable_rate_confirm', { user: label });
    if (!confirm(confirmText)) return;

    try {
        await apiRequest(`/api/admin/users/${user.id}/can-rate`, {
            method: 'POST',
            token: state.token,
            body: { canRate: next },
        });
        await loadUsers(searchInput?.value ?? '');
    } catch (err) {
        alert(t('admin.rate_toggle_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function toggleBan(user: AdminUser): Promise<void> {
    const label = user.username || user.email;
    const banning = !user.banned_at;
    if (!confirm(t(banning ? 'admin.ban_confirm' : 'admin.unban_confirm', { user: label }))) return;

    try {
        await apiRequest(`/api/admin/users/${user.id}/${banning ? 'ban' : 'unban'}`, {
            method: 'POST',
            token: state.token,
        });
        await loadUsers(searchInput?.value ?? '');
    } catch (err) {
        alert(t('admin.ban_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function deleteUser(user: AdminUser): Promise<void> {
    const label = user.username || user.email;
    if (!confirm(t('admin.delete_user_confirm', { user: label }))) return;

    try {
        await apiRequest(`/api/admin/users/${user.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        await loadUsers(searchInput?.value ?? '');
    } catch (err) {
        alert(t('admin.delete_user_error') + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Архив удалённых оценок
// ============================================================
async function loadArchive(): Promise<void> {
    if (!archiveBox) return;
    archiveBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    const q = archiveSearchInput?.value.trim() ?? '';
    const url = q
        ? `/api/admin/ratings/archive?q=${encodeURIComponent(q)}&limit=200`
        : '/api/admin/ratings/archive?limit=200';

    try {
        const res = await apiRequest<AdminArchiveResponse>(url, { token: state.token });
        renderArchive(res.archived, res.total);
    } catch (err) {
        archiveBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderArchive(rows: AdminArchivedRating[], total: number): void {
    if (!archiveBox) return;
    archiveBox.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'admin-archive-total';
    head.textContent = t('admin.archive_total', { n: total });
    archiveBox.appendChild(head);

    if (rows.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('admin.archive_empty');
        archiveBox.appendChild(p);
        return;
    }

    for (const r of rows) {
        const row = document.createElement('div');
        row.className = 'admin-archive-row';

        const info = document.createElement('div');
        info.className = 'admin-archive-info';

        const who = document.createElement('div');
        who.className = 'admin-archive-who';
        who.textContent = `${r.user_name ?? r.user_id} → ${r.player_name ?? r.player_id}`;

        const statLine = document.createElement('div');
        statLine.className = 'admin-archive-stats';
        statLine.textContent =
            `${r.race} · A${r.adaptiveness} G${r.greed} S${r.survival} T${r.turtle} X${r.aggression} V${r.variety}`;

        const when = document.createElement('div');
        when.className = 'admin-archive-when';
        when.textContent = `${t('admin.archive_deleted')}: ${new Date(r.deleted_at).toLocaleString()}`;

        info.append(who, statLine, when);
        row.appendChild(info);

        const actions = document.createElement('div');
        actions.className = 'admin-archive-actions';

        const restoreBtn = document.createElement('button');
        restoreBtn.type = 'button';
        restoreBtn.className = 'btn-secondary';
        restoreBtn.textContent = `↩ ${t('admin.archive_restore')}`;
        restoreBtn.addEventListener('click', () => void restoreRating(r));
        actions.appendChild(restoreBtn);

        const restoreAllBtn = document.createElement('button');
        restoreAllBtn.type = 'button';
        restoreAllBtn.className = 'btn-secondary';
        restoreAllBtn.textContent = `↩ ${t('admin.archive_restore_all')}`;
        restoreAllBtn.title = t('admin.archive_restore_all_title');
        restoreAllBtn.addEventListener('click', () => void restoreUserRatings(r.user_id));
        actions.appendChild(restoreAllBtn);

        row.appendChild(actions);
        archiveBox.appendChild(row);
    }
}

async function restoreRating(r: AdminArchivedRating): Promise<void> {
    if (!confirm(t('admin.archive_restore_confirm'))) return;
    try {
        await apiRequest(`/api/admin/ratings/${r.id}/restore`, {
            method: 'POST',
            token: state.token,
        });
        await loadArchive();
    } catch (err) {
        alert(t('admin.archive_restore_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function restoreUserRatings(userId: string): Promise<void> {
    if (!confirm(t('admin.archive_restore_all_confirm'))) return;
    try {
        await apiRequest(`/api/admin/ratings/archive/restore-user/${encodeURIComponent(userId)}`, {
            method: 'POST',
            token: state.token,
        });
        await loadArchive();
    } catch (err) {
        alert(t('admin.archive_restore_error') + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Корзина: удалённые игры и моды
// ============================================================
async function loadTrash(): Promise<void> {
    const box = document.getElementById('admin-trash');
    if (!box) return;
    box.innerHTML = '<div class="skeleton skeleton-block"></div>';
    let items: AdminDeletedItem[];
    try {
        items = (await apiRequest<{ items: AdminDeletedItem[] }>('/api/admin/deleted', { token: state.token })).items;
    } catch (err) {
        box.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)}</p>`;
        return;
    }
    box.innerHTML = '';
    if (items.length === 0) {
        const p = document.createElement('p');
        p.className = 'hint';
        p.textContent = t('admin.trash_empty');
        box.appendChild(p);
        return;
    }
    for (const item of items) {
        const row = document.createElement('div');
        row.className = 'admin-archive-row';

        const info = document.createElement('div');
        info.className = 'admin-archive-info';
        const who = document.createElement('div');
        who.className = 'admin-archive-who';
        who.textContent = `${item.entity_type === 'game' ? t('admin.trash_game') : t('admin.trash_mod')}: ${item.title}`;
        const when = document.createElement('div');
        when.className = 'admin-archive-when';
        when.textContent = `${t('admin.archive_deleted')}: ${new Date(item.deleted_at).toLocaleString()}`
            + (item.deleted_by_name ? ` · ${t('admin.trash_deleted_by', { name: item.deleted_by_name })}` : '');
        info.append(who, when);
        row.appendChild(info);

        const actions = document.createElement('div');
        actions.className = 'admin-archive-actions';
        const restoreBtn = document.createElement('button');
        restoreBtn.type = 'button';
        restoreBtn.className = 'btn-secondary';
        restoreBtn.textContent = `↩ ${t('admin.archive_restore')}`;
        restoreBtn.addEventListener('click', () => void restoreTrashItem(item, restoreBtn));
        actions.appendChild(restoreBtn);
        row.appendChild(actions);

        box.appendChild(row);
    }
}

async function restoreTrashItem(item: AdminDeletedItem, btn: HTMLButtonElement): Promise<void> {
    if (!confirm(t('admin.trash_restore_confirm', { title: item.title }))) return;
    btn.disabled = true;
    try {
        await apiRequest(`/api/admin/deleted/${item.id}/restore`, { method: 'POST', token: state.token });
        await loadTrash();
    } catch (err) {
        btn.disabled = false;
        alert(t('admin.trash_restore_error') + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Игроки
// ============================================================
async function loadPlayers(): Promise<void> {
    if (!playersBox) return;
    playersBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const res = await apiRequest<PlayersListResponse>('/api/players', { token: state.token });
        cachedPlayers = res.players;
        renderPlayersFiltered();
    } catch (err) {
        playersBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderPlayersFiltered(): void {
    const q = (playersSearchInput?.value ?? '').trim().toLowerCase();
    const filtered = q
        ? cachedPlayers.filter((p) =>
            p.name.toLowerCase().includes(q) ||
            (p.aka ?? '').toLowerCase().includes(q)
        )
        : cachedPlayers;
    renderPlayers(filtered);
}

function renderPlayers(players: PlayerWithStats[]): void {
    if (!playersBox) return;

    if (players.length === 0) {
        playersBox.innerHTML = `<p class="hint">${t('players.no_players')}</p>`;
        return;
    }

    playersBox.innerHTML = '';

    const table = document.createElement('table');
    table.className = 'admin-players-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const label of ['', t('players.col.name'), t('player.votes'), t('players.col.total'), '']) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const p of players) {
        const tr = document.createElement('tr');
        tr.className = 'admin-player-row';

        const tdExpand = document.createElement('td');
        tdExpand.className = 'admin-expand-cell';
        const expandBtn = document.createElement('button');
        expandBtn.type = 'button';
        expandBtn.className = 'admin-expand-btn';
        expandBtn.textContent = '▸';
        tdExpand.appendChild(expandBtn);
        tr.appendChild(tdExpand);

        const tdName = document.createElement('td');

        const nameWrap = document.createElement('div');
        nameWrap.className = 'admin-player-name';

        const nameText = document.createElement('span');
        nameText.textContent = p.name;
        nameWrap.appendChild(nameText);

        if (p.user_id) {
            const linked = document.createElement('span');
            linked.className = 'admin-linked-badge';
            linked.textContent = `🔗 ${p.username || t('admin.linked_player')}`;
            linked.title = p.user_id;
            nameWrap.appendChild(linked);
        }

        tdName.appendChild(nameWrap);
        tr.appendChild(tdName);

        const tdVotes = document.createElement('td');
        tdVotes.textContent = String(p.vote_count);
        tr.appendChild(tdVotes);

        const tdTotal = document.createElement('td');
        const total = ['adaptiveness', 'greed', 'survival', 'turtle', 'aggression', 'variety']
            .reduce((sum, key) => sum + ((p as any)[key] ?? 0), 0);
        tdTotal.textContent = p.vote_count > 0 ? total.toFixed(2) : '—';
        tr.appendChild(tdTotal);

        const tdActions = document.createElement('td');
        tdActions.className = 'admin-player-actions';

        const renameBtn = document.createElement('button');
        renameBtn.type = 'button';
        renameBtn.className = 'admin-rename-btn';
        renameBtn.textContent = `✎ ${t('admin.rename_button')}`;
        renameBtn.addEventListener('click', () => void renamePlayer(p));
        tdActions.appendChild(renameBtn);

        const akaBtn = document.createElement('button');
        akaBtn.type = 'button';
        akaBtn.className = 'admin-aka-btn';
        akaBtn.textContent = `✎ ${t('admin.aka_button')}`;
        akaBtn.addEventListener('click', () => void editAka(p));
        tdActions.appendChild(akaBtn);

        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'btn-danger';
        delBtn.textContent = `🗑 ${t('admin.delete_button')}`;
        delBtn.addEventListener('click', () => void deletePlayer(p));
        tdActions.appendChild(delBtn);

        const linkBtn = document.createElement('button');
        linkBtn.type = 'button';
        linkBtn.className = 'admin-link-btn';
        if (p.user_id) linkBtn.classList.add('is-linked');
        linkBtn.textContent = p.user_id
            ? `⛓ ${t('admin.unlink_user')}`
            : `🔗 ${t('admin.link_user')}`;
        linkBtn.title = p.user_id
            ? t('admin.unlink_user_title')
            : t('admin.link_user_title');
        linkBtn.addEventListener('click', () => {
            if (p.user_id) {
                void unlinkUser(p);
            } else {
                void linkUser(p);
            }
        });
        tdActions.appendChild(linkBtn);

        tr.appendChild(tdActions);
        tbody.appendChild(tr);

        const trRatings = document.createElement('tr');
        trRatings.className = 'admin-ratings-row hidden';
        const tdRatings = document.createElement('td');
        tdRatings.colSpan = 5;
        tdRatings.className = 'admin-ratings-cell';
        trRatings.appendChild(tdRatings);
        tbody.appendChild(trRatings);

        let loaded = false;
        expandBtn.addEventListener('click', () => {
            const isOpen = !trRatings.classList.contains('hidden');
            if (isOpen) {
                trRatings.classList.add('hidden');
                expandBtn.textContent = '▸';
                return;
            }
            trRatings.classList.remove('hidden');
            expandBtn.textContent = '▾';
            if (!loaded) {
                loaded = true;
                void loadPlayerRatings(p.id, tdRatings);
            }
        });
    }
    table.appendChild(tbody);
    playersBox.appendChild(table);
}

async function loadPlayerRatings(playerId: number, container: HTMLElement): Promise<void> {
    container.innerHTML = '<div class="skeleton skeleton-block" style="height: 80px;"></div>';

    try {
        const res = await apiRequest<{ ratings: AdminRating[] }>(
            `/api/admin/players/${playerId}/ratings`,
            { token: state.token }
        );
        renderRatingsInAdmin(res.ratings, container, playerId);
    } catch (err) {
        container.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderRatingsInAdmin(
    ratings: AdminRating[],
    container: HTMLElement,
    playerId: number
): void {
    if (ratings.length === 0) {
        container.innerHTML = `<p class="hint">${t('admin.ratings_empty')}</p>`;
        return;
    }

    const table = document.createElement('table');
    table.className = 'admin-ratings-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const label of [
        t('ratings.col.user'), t('ratings.col.race'),
        t('stat.adaptiveness'), t('stat.greed'), t('stat.survival'),
        t('stat.turtle'), t('stat.aggression'), t('stat.variety'),
        ''
    ]) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const r of ratings) {
        const tr = document.createElement('tr');

        const tdUser = document.createElement('td');
        tdUser.textContent = r.username || r.email || r.user_id;
        tr.appendChild(tdUser);

        const tdRace = document.createElement('td');
        tdRace.textContent = r.race;
        tr.appendChild(tdRace);

        for (const key of ['adaptiveness', 'greed', 'survival', 'turtle', 'aggression', 'variety'] as const) {
            const td = document.createElement('td');
            td.textContent = String(r[key]);
            tr.appendChild(td);
        }

        const tdActions = document.createElement('td');
        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'admin-rating-delete-btn';
        delBtn.textContent = '🗑';
        delBtn.addEventListener('click', () => void deleteRating(r, container, playerId));
        tdActions.appendChild(delBtn);
        tr.appendChild(tdActions);

        tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    container.innerHTML = '';
    container.appendChild(table);
}

async function deleteRating(rating: AdminRating, container: HTMLElement, playerId: number): Promise<void> {
    const userLabel = rating.username || rating.email || rating.user_id;
    if (!confirm(t('admin.rating_delete_confirm', { user: userLabel }))) return;

    try {
        await apiRequest(`/api/admin/ratings/${rating.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        await loadPlayerRatings(playerId, container);
        await loadPlayers();
    } catch (err) {
        alert(t('games.delete_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function deletePlayer(p: PlayerWithStats): Promise<void> {
    if (!confirm(t('admin.player_delete_confirm', { name: p.name, count: p.vote_count }))) return;

    try {
        await apiRequest(`/api/players/${p.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        await loadPlayers();
    } catch (err) {
        alert(t('games.delete_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function renamePlayer(p: PlayerWithStats): Promise<void> {
    const next = prompt(t('player.name_title', { name: p.name }), p.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed) {
        alert(t('player.name_empty_error'));
        return;
    }
    if (trimmed === p.name) return;

    try {
        await apiRequest(`/api/players/${p.id}/name`, {
            method: 'PATCH',
            token: state.token,
            body: { name: trimmed },
        });
        await loadPlayers();
    } catch (err) {
        alert(t('player.name_save_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function editAka(p: PlayerWithStats): Promise<void> {
    const next = prompt(t('player.aka_title', { name: p.name }), p.aka ?? '');
    if (next === null) return;

    try {
        await apiRequest(`/api/players/${p.id}/aka`, {
            method: 'PATCH',
            token: state.token,
            body: { aka: next.trim() || null },
        });
        await loadPlayers();
    } catch (err) {
        alert(t('player.aka_save_error') + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Универсальные справочники
// ============================================================
async function loadRefs(config: RefConfig): Promise<void> {
    const box = document.getElementById(config.containerId);
    if (!box) return;
    box.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const items = await listRefs(config.type);
        refsCache[config.type] = items;
        renderRefs(config);
    } catch {
        box.innerHTML = `<p class="error">${t('common.error')}</p>`;
    }
}

function renderRefs(config: RefConfig): void {
    const box = document.getElementById(config.containerId);
    if (!box) return;

    let items = refsCache[config.type];

    // Подсказки авторов для поля «Автор» берём из полного списка карт/модов.
    if (config.type === 'maps') {
        setMapAuthors(refsCache.maps.map((m) => (m as GameMap).author));
    } else if (config.type === 'mods') {
        setModAuthors(refsCache.mods.map((m) => (m as GameMod).author));
    }

    // Поиск по названию (карты — ещё и по альт. названию)
    const q = (refSearchInputs[config.type]?.value ?? '').trim().toLowerCase();
    if (q) {
        items = items.filter((item) => {
            if (item.name.toLowerCase().includes(q)) return true;
            const alt = (item as GameMap).alt_name;
            return config.type === 'maps' && !!alt && alt.toLowerCase().includes(q);
        });
    }

    if (items.length === 0) {
        box.innerHTML = `<p class="hint">${t('common.empty')}</p>`;
        return;
    }

    box.innerHTML = '';
    for (const item of items) {
        box.appendChild(buildRefCard(config, item));
    }
}

// ============================================================
// Автосохранение карт (без кнопки «Сохранить» и перезагрузки списка)
// Правка сразу пишется в кэш, на сервер уходит с дебаунсом; обновляется
// только индикатор карточки — список не перерисовывается.
// ============================================================
const refDebounce = new Map<number, ReturnType<typeof setTimeout>>();
const REF_AUTOSAVE_DELAY = 600;

function setRefStatus(id: number, status: 'idle' | 'saving' | 'saved' | 'error'): void {
    const card = document.querySelector<HTMLElement>(`.admin-ref-card[data-ref-id="${id}"]`);
    const el = card?.querySelector('.ref-save-status') as HTMLElement | null;
    if (!el) return;
    el.classList.remove('is-saving', 'is-saved', 'is-error');
    if (status === 'saving') {
        el.classList.add('is-saving');
        el.textContent = '…';
        el.title = t('common.saving');
    } else if (status === 'saved') {
        el.classList.add('is-saved');
        el.textContent = '✓';
        el.title = t('common.saved');
    } else if (status === 'error') {
        el.classList.add('is-error');
        el.textContent = '✗';
        el.title = t('common.error');
    } else {
        el.textContent = '';
        el.title = '';
    }
}

/** Правит карточку в кэше и ставит отложенное сохранение. */
function patchRefItem(item: RefItem, patch: Record<string, unknown>): void {
    Object.assign(item, patch);
    const prev = refDebounce.get(item.id);
    if (prev) clearTimeout(prev);
    refDebounce.set(item.id, setTimeout(() => void saveRefItem(item), REF_AUTOSAVE_DELAY));
    setRefStatus(item.id, 'saving');
}

/** Сохраняет карту на сервере, не перерисовывая список. */
async function saveRefItem(item: RefItem): Promise<void> {
    const name = String(item.name ?? '').trim();
    if (!name) return; // пустое имя — ждём, пока пользователь введёт
    item.name = name;
    try {
        const updated = await updateRef<GameMap>('maps', item.id, {
            name,
            alt_name: (item as GameMap).alt_name ?? null,
            map_types: (item as GameMap).map_types ?? [],
            team_formats: (item as GameMap).team_formats ?? [],
            player_count: (item as GameMap).player_count ?? null,
            author: (item as GameMap).author ?? null,
            link: (item as GameMap).link ?? null,
        }, state.token);
        Object.assign(item, updated);
        setRefStatus(item.id, 'saved');
    } catch (err) {
        setRefStatus(item.id, 'error');
        console.error('[admin/refs] autosave error:', err);
    }
}

function buildRefCard(config: RefConfig, item: RefItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'admin-ref-card';
    card.dataset.refId = String(item.id);

    // Карты сохраняются автоматически — без кнопки и перезагрузки списка.
    const isMap = config.type === 'maps';
    if (isMap) {
        card.classList.add('admin-ref-card--map');
        const status = document.createElement('span');
        status.className = 'ref-save-status';
        card.appendChild(status);
    }

    const isDeletedMap = config.type === 'maps' && !!(item as GameMap).deleted_at;
    if (isDeletedMap) card.classList.add('is-deleted');

    // --- Название (редактируемое) ---
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'admin-ref-input';
    nameInput.value = item.name;
    if (isMap) {
        nameInput.addEventListener('input', () => {
            patchRefItem(item, { name: nameInput.value });
        });
    }
    card.appendChild(nameInput);

    if (isDeletedMap) {
        const badge = document.createElement('span');
        badge.className = 'admin-ref-deleted-badge';
        badge.textContent = t('admin.map_deleted_badge');
        card.appendChild(badge);
    }

    // --- Доп. поля ---
    const extraInputs: Record<string, HTMLInputElement | HTMLSelectElement> = {};

    for (const field of config.editableFields) {
        const input = document.createElement('input');
        input.className = 'admin-ref-input';

        if (field === 'slug') {
            input.type = 'text';
            input.value = (item as GameFormat).slug ?? '';
            input.placeholder = 'slug';
        } else if (field === 'elo_weight') {
            input.type = 'number';
            input.className = 'admin-ref-input admin-ref-input--small';
            input.value = String((item as GameFormat).elo_weight ?? 1.0);
            input.step = '0.05';
            input.min = '0.1';
            input.max = '2.0';
            input.title = t('admin.elo_weight');
        } else if (field === 'aka') {
            input.type = 'text';
            input.value = (item as GameHost).aka ?? '';
            input.placeholder = 'aka';
        } else if (field === 'alt_name') {
            input.type = 'text';
            input.value = (item as GameMap).alt_name ?? '';
            input.placeholder = t('admin.alt_name_placeholder');
            input.addEventListener('input', () => {
                patchRefItem(item, { alt_name: input.value.trim() || null });
            });
        } else if (field === 'author') {
            input.type = 'text';
            input.classList.add('admin-ref-author-input');
            input.value = (item as GameMap | GameMod).author ?? '';
            input.placeholder = t('admin.map_author_placeholder');
            // У карт автор сохраняется автосейвом; у модов — кнопкой «Сохранить».
            if (isMap) {
                input.addEventListener('input', () => {
                    patchRefItem(item, { author: input.value.trim() || null });
                });
            }
        } else if (field === 'description') {
            input.type = 'text';
            input.value = (item as GameMod).description ?? '';
            input.placeholder = t('mods.field_description');
        } else if (field === 'link') {
            input.type = 'text';
            input.value = (item as GameMap).link ?? '';
            input.placeholder = t('admin.map_link_placeholder');
            input.addEventListener('input', () => {
                patchRefItem(item, { link: input.value.trim() || null });
            });
        } else if (field === 'player_count') {
            // Число игроков карты (может быть не задано)
            const num = document.createElement('input');
            num.type = 'number';
            num.min = '1';
            num.max = '64';
            num.className = 'admin-ref-input admin-ref-input--small';
            num.placeholder = t('admin.map_players_placeholder');
            num.title = t('admin.map_players_title');
            num.value = (item as GameMap).player_count != null ? String((item as GameMap).player_count) : '';
            num.addEventListener('input', () => {
                const v = num.value.trim();
                patchRefItem(item, { player_count: v === '' ? null : Number(v) });
            });
            card.appendChild(num);
            continue;
        } else if (field === 'map_types') {
            // Независимые типы карты (галочки): обычная / командная / КБ
            const types = document.createElement('div');
            types.className = 'admin-ref-types';
            for (const type of MAP_TYPES) {
                const label = document.createElement('label');
                label.className = 'admin-ref-type';
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.checked = ((item as GameMap).map_types ?? []).includes(type);
                cb.addEventListener('change', () => {
                    const list = ((item as GameMap).map_types ?? []).slice();
                    const i = list.indexOf(type);
                    if (cb.checked && i < 0) list.push(type);
                    if (!cb.checked && i >= 0) list.splice(i, 1);
                    // Сняли «Командная» — форматы больше не актуальны
                    const clearFormats = !cb.checked && type === 'team';
                    patchRefItem(
                        item,
                        clearFormats ? { map_types: list, team_formats: [] } : { map_types: list }
                    );
                    if (clearFormats) {
                        card.querySelectorAll<HTMLInputElement>('.admin-ref-formats input[type=checkbox]')
                            .forEach((f) => { f.checked = false; });
                    }
                });
                const span = document.createElement('span');
                span.textContent = t(`map_type.${type}` as any);
                label.append(cb, span);
                types.appendChild(label);
            }
            card.appendChild(types);
            continue;
        } else if (field === 'team_formats') {
            // Формат командной карты (2на2 / 3на3 / 4на4 / 2на2на2 / 2на2на2на2)
            const formats = document.createElement('div');
            formats.className = 'admin-ref-types admin-ref-formats';
            formats.title = t('admin.map_formats_title');
            for (const format of TEAM_FORMATS) {
                const label = document.createElement('label');
                label.className = 'admin-ref-type';
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.checked = ((item as GameMap).team_formats ?? []).includes(format);
                cb.addEventListener('change', () => {
                    const list = ((item as GameMap).team_formats ?? []).slice();
                    const i = list.indexOf(format);
                    if (cb.checked && i < 0) list.push(format);
                    if (!cb.checked && i >= 0) list.splice(i, 1);
                    patchRefItem(item, { team_formats: list });
                });
                const span = document.createElement('span');
                span.textContent = teamFormatLabel(format);
                label.append(cb, span);
                formats.appendChild(label);
            }
            card.appendChild(formats);
            continue;
        } else if (field === 'player_id') {
            // Ведущий → привязка к игроку (стример): выбор из списка игроков
            const select = document.createElement('select');
            select.className = 'admin-ref-input';
            select.title = t('admin.host_player_title');

            const none = document.createElement('option');
            none.value = '';
            none.textContent = t('admin.host_player_none');
            select.appendChild(none);

            for (const p of cachedPlayers) {
                const opt = document.createElement('option');
                opt.value = String(p.id);
                opt.textContent = p.name;
                if (p.id === (item as GameHost).player_id) opt.selected = true;
                select.appendChild(opt);
            }

            extraInputs[field] = select;
            card.appendChild(select);
            continue;
        }

        extraInputs[field] = input;
        card.appendChild(input);
    }

    // Автор (карты и моды): выпадающие подсказки по уже существующим авторам
    const authorField = card.querySelector<HTMLInputElement>('.admin-ref-author-input');
    if (authorField) {
        attachAutocomplete(authorField, config.type === 'mods' ? getModAuthors : getMapAuthors);
    }

    // --- Картинка карты (загрузка + предпросмотр при наведении) ---
    if (isMap) {
        card.appendChild(buildMapImageControl(config, item as GameMap));
    }

    // --- Кнопки ---
    const actions = document.createElement('div');
    actions.className = 'admin-ref-actions';

    // У карт кнопки «Сохранить» нет — правки сохраняются автоматически.
    if (!isMap) {
        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.className = 'admin-ref-btn admin-ref-btn--save';
        saveBtn.textContent = '✓';
        saveBtn.title = t('common.save');
        saveBtn.addEventListener('click', () => {
            void saveRef(config, item, nameInput.value, extraInputs);
        });
        actions.appendChild(saveBtn);
    }

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'admin-ref-btn admin-ref-btn--delete';
    if (isDeletedMap) {
        delBtn.textContent = '↩';
        delBtn.title = t('admin.restore');
        delBtn.classList.add('admin-ref-btn--restore');
        delBtn.addEventListener('click', () => {
            void restoreRefItem(config, item);
        });
    } else {
        delBtn.textContent = '🗑';
        delBtn.title = t('common.delete');
        delBtn.addEventListener('click', () => {
            void deleteRefItem(config, item);
        });
    }
    actions.appendChild(delBtn);

    card.appendChild(actions);
    return card;
}

/** Перерисовывает только одну карточку справочника (без перезагрузки списка). */
function rerenderRefCard(config: RefConfig, item: RefItem): void {
    const old = document.querySelector<HTMLElement>(`.admin-ref-card[data-ref-id="${item.id}"]`);
    if (!old) return;
    old.replaceWith(buildRefCard(config, item));
}

// --- Предпросмотр картинки карты при наведении ---
let mapImagePreview: HTMLElement | null = null;

function hideMapImagePreview(): void {
    mapImagePreview?.remove();
    mapImagePreview = null;
}

function showMapImagePreview(src: string, anchor: HTMLElement): void {
    hideMapImagePreview();
    const box = document.createElement('div');
    box.className = 'admin-map-preview';
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    box.appendChild(img);
    document.body.appendChild(box);
    const r = anchor.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    const pad = 8;

    let left = r.left;
    if (left + b.width > window.innerWidth - pad) left = window.innerWidth - b.width - pad;
    if (left < pad) left = pad;

    let top = r.bottom + 8;
    if (top + b.height > window.innerHeight - pad) top = r.top - b.height - 8;
    if (top < pad) top = pad;

    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    mapImagePreview = box;
}

/** Кнопка загрузки/замены картинки и миниатюра с предпросмотром. */
function buildMapImageControl(config: RefConfig, item: GameMap): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'admin-map-image';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.className = 'admin-map-file';
    fileInput.hidden = true;

    fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        fileInput.value = '';
        if (!file) return;
        setRefStatus(item.id, 'saving');
        try {
            const updated = await uploadMapImage(item.id, file, state.token);
            Object.assign(item, updated);
            setRefStatus(item.id, 'saved');
            rerenderRefCard(config, item);
        } catch (err) {
            setRefStatus(item.id, 'error');
            alert(t('admin.map_image_error') + (err instanceof Error ? err.message : String(err)));
        }
    });

    if (item.image_url) {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'admin-map-thumb';
        thumb.title = t('admin.map_image_hover');
        const img = document.createElement('img');
        img.src = item.image_url;
        img.alt = '';
        thumb.appendChild(img);
        thumb.addEventListener('mouseenter', () => showMapImagePreview(item.image_url!, thumb));
        thumb.addEventListener('focus', () => showMapImagePreview(item.image_url!, thumb));
        thumb.addEventListener('mouseleave', hideMapImagePreview);
        thumb.addEventListener('blur', hideMapImagePreview);
        thumb.addEventListener('click', () => fileInput.click());
        wrap.appendChild(thumb);

        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'admin-map-img-remove';
        rm.title = t('admin.map_image_remove');
        rm.textContent = '✕';
        rm.addEventListener('click', async () => {
            if (!confirm(t('admin.map_image_remove_confirm', { name: item.name }))) return;
            hideMapImagePreview();
            setRefStatus(item.id, 'saving');
            try {
                const updated = await deleteMapImage(item.id, state.token);
                Object.assign(item, updated);
                setRefStatus(item.id, 'saved');
                rerenderRefCard(config, item);
            } catch (err) {
                setRefStatus(item.id, 'error');
                console.error('[admin/refs] map image delete error:', err);
            }
        });
        wrap.appendChild(rm);
    } else {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'admin-map-thumb admin-map-thumb--empty';
        btn.title = t('admin.map_image_upload');
        btn.textContent = '🖼';
        btn.addEventListener('click', () => fileInput.click());
        wrap.appendChild(btn);
    }

    wrap.appendChild(fileInput);
    return wrap;
}

async function addRefInline(config: RefConfig): Promise<void> {
    const name = prompt(t(config.newPromptKey as any));
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    const payload: Record<string, unknown> = { name: trimmed };

    if (config.type === 'formats') {
        const slug = trimmed
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        payload.slug = slug || `format-${Date.now()}`;
        payload.sort_order = 100;
        payload.elo_weight = 1.0;
    }

    try {
        await createRef(config.type, payload, state.token);
        await loadRefs(config);
    } catch (err) {
        alert(t(config.newErrorKey as any) + (err instanceof Error ? err.message : String(err)));
    }
}

async function saveRef(
    config: RefConfig,
    item: RefItem,
    newName: string,
    extraInputs: Record<string, HTMLInputElement | HTMLSelectElement>
): Promise<void> {
    const trimmedName = newName.trim();
    if (!trimmedName) {
        alert(t('common.error') + ': name is empty');
        return;
    }

    const payload: Record<string, unknown> = { name: trimmedName };

    for (const [field, input] of Object.entries(extraInputs)) {
        if (field === 'slug') payload.slug = input.value.trim();
        else if (field === 'elo_weight') payload.elo_weight = Number(input.value);
        else if (field === 'aka') payload.aka = input.value.trim() || null;
        else if (field === 'alt_name') payload.alt_name = input.value.trim() || null;
        else if (field === 'author') payload.author = input.value.trim() || null;
        else if (field === 'description') payload.description = input.value.trim() || null;
        else if (field === 'player_id') {
            payload.player_id = input.value ? Number(input.value) : null;
        }
    }

    try {
        await updateRef(config.type, item.id, payload, state.token);
        await loadRefs(config);
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

async function deleteRefItem(config: RefConfig, item: RefItem): Promise<void> {
    if (!confirm(t(config.deleteConfirmKey as any, { name: item.name }))) return;

    try {
        await deleteRef(config.type, item.id, state.token);
        await loadRefs(config);
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

async function restoreRefItem(config: RefConfig, item: RefItem): Promise<void> {
    try {
        await updateRef(config.type, item.id, { deleted_at: null }, state.token);
        await loadRefs(config);
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Титулы
// Автocохранение: правки коммитятся в titlesCache сразу и уходят на сервер
// с дебаунсом — кнопку «Сохранить» жать не нужно. Обновляется только
// затронутая карточка, поэтому правки других титулов не теряются.
// ============================================================
let titlesCache: AdminTitle[] = [];
const titleDebounce = new Map<number, ReturnType<typeof setTimeout>>();
const AUTOSAVE_DELAY = 600;

function getTitle(id: number): AdminTitle | undefined {
    return titlesCache.find((t) => t.id === id);
}

function setTitleStatus(id: number, status: 'idle' | 'saving' | 'saved' | 'error'): void {
    const card = titlesBox?.querySelector(`[data-title-id="${id}"]`);
    const el = card?.querySelector('.title-save-status') as HTMLElement | null;
    if (!el) return;
    el.classList.remove('is-saving', 'is-saved', 'is-error');
    if (status === 'saving') {
        el.classList.add('is-saving');
        el.textContent = '…';
        el.title = t('common.saving');
    } else if (status === 'saved') {
        el.classList.add('is-saved');
        el.textContent = '✓';
        el.title = t('common.saved');
    } else if (status === 'error') {
        el.classList.add('is-error');
        el.textContent = '✗';
        el.title = t('common.error');
    } else {
        el.textContent = '';
        el.title = '';
    }
}

function scheduleSave(id: number): void {
    const prev = titleDebounce.get(id);
    if (prev) clearTimeout(prev);
    titleDebounce.set(id, setTimeout(() => void saveTitle(id), AUTOSAVE_DELAY));
    setTitleStatus(id, 'saving');
}

function patchTitle(id: number, patch: Partial<AdminTitle>): void {
    const t = getTitle(id);
    if (!t) return;
    Object.assign(t, patch);
    scheduleSave(id);
}

async function loadTitles(): Promise<void> {
    if (!titlesBox) return;
    titlesBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const res = await apiRequest<{ items: AdminTitle[] }>('/api/titles');
        titlesCache = res.items;
        renderTitles();
    } catch (err) {
        titlesBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderTitles(): void {
    if (!titlesBox) return;

    if (titlesCache.length === 0) {
        titlesBox.innerHTML = `<p class="hint">${t('admin.titles_empty')}</p>`;
        return;
    }

    titlesBox.innerHTML = '';
    for (const title of titlesCache) {
        titlesBox.appendChild(buildTitleCard(title));
    }
}

/** Пересобирает только одну карточку из кэша (правки соседних не трогает). */
function replaceTitleCard(id: number): void {
    if (!titlesBox) return;
    const old = titlesBox.querySelector(`[data-title-id="${id}"]`);
    const t = getTitle(id);
    if (!t) return;
    const fresh = buildTitleCard(t);
    if (old) old.replaceWith(fresh);
    else titlesBox.appendChild(fresh);
}

function buildTitleCard(title: AdminTitle): HTMLElement {
    const card = document.createElement('div');
    card.className = 'admin-title-card';
    card.dataset.titleId = String(title.id);

    // Индикатор сохранения
    const status = document.createElement('span');
    status.className = 'title-save-status';
    card.appendChild(status);

    // --- Текст титула ---
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'admin-ref-input';
    nameInput.value = title.name;
    nameInput.addEventListener('input', () => {
        patchTitle(title.id, { name: nameInput.value });
    });
    card.appendChild(nameInput);

    // --- Перевод на английский ---
    const nameEnInput = document.createElement('input');
    nameEnInput.type = 'text';
    nameEnInput.className = 'admin-ref-input';
    nameEnInput.value = title.name_en ?? '';
    nameEnInput.placeholder = t('admin.title_name_en_placeholder');
    nameEnInput.title = t('admin.title_name_en_title');
    nameEnInput.addEventListener('input', () => {
        patchTitle(title.id, { name_en: nameEnInput.value });
    });
    card.appendChild(nameEnInput);

    // --- Палитра цветов ---
    const swatches = document.createElement('div');
    swatches.className = 'title-swatches';
    for (const c of TITLE_COLORS) {
        const sw = document.createElement('button');
        sw.type = 'button';
        sw.className = 'title-swatch' + (c.value.toLowerCase() === title.color.toLowerCase() ? ' is-active' : '');
        sw.style.setProperty('--swatch', c.value);
        sw.title = t(c.nameKey as any);
        sw.addEventListener('click', () => {
            for (const s of swatches.querySelectorAll('.title-swatch')) s.classList.remove('is-active');
            sw.classList.add('is-active');
            patchTitle(title.id, { color: c.value });
        });
        swatches.appendChild(sw);
    }
    card.appendChild(swatches);

    // --- Размер бейджа ---
    const sizeRow = document.createElement('div');
    sizeRow.className = 'title-size-row';
    const sizeLabel = document.createElement('span');
    sizeLabel.className = 'title-size-label';
    sizeLabel.textContent = t('title.size_label');
    const sizeSelect = document.createElement('select');
    sizeSelect.className = 'admin-ref-input';
    for (const s of TITLE_SIZES) {
        const opt = document.createElement('option');
        opt.value = s;
        opt.textContent = t(`title.size_${s}` as any);
        if (s === (title.size ?? 'small')) opt.selected = true;
        sizeSelect.appendChild(opt);
    }
    sizeSelect.addEventListener('change', () => {
        patchTitle(title.id, { size: sizeSelect.value as TitleSize });
    });
    sizeRow.append(sizeLabel, sizeSelect);
    card.appendChild(sizeRow);

    // --- Назначенные игроки ---
    const playersEl = document.createElement('div');
    playersEl.className = 'title-players';

    const assigned = title.player_ids
        .map((id) => cachedPlayers.find((p) => p.id === id))
        .filter((p): p is PlayerWithStats => !!p);

    if (assigned.length > 0) {
        for (const p of assigned) {
            const chip = document.createElement('span');
            chip.className = 'title-player-chip';
            chip.textContent = p.name;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'title-player-remove';
            removeBtn.textContent = '×';
            removeBtn.title = t('admin.title_unassign');
            removeBtn.addEventListener('click', () => {
                void unassignPlayerFromTitle(title.id, p.id);
            });
            chip.appendChild(removeBtn);
            playersEl.appendChild(chip);
        }
    }

    // --- Добавить игрока ---
    const addRow = document.createElement('div');
    addRow.className = 'title-add-player';

    const select = document.createElement('select');
    select.className = 'admin-ref-input';
    const none = document.createElement('option');
    none.value = '';
    none.textContent = t('admin.title_select_player');
    select.appendChild(none);
    for (const p of cachedPlayers) {
        if (title.player_ids.includes(p.id)) continue;
        const opt = document.createElement('option');
        opt.value = String(p.id);
        opt.textContent = p.name;
        select.appendChild(opt);
    }
    addRow.appendChild(select);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn-secondary';
    addBtn.textContent = t('admin.title_add_player');
    addBtn.addEventListener('click', () => {
        const pid = Number(select.value);
        if (!pid) return;
        void assignPlayerToTitle(title.id, pid);
    });
    addRow.appendChild(addBtn);
    playersEl.appendChild(addRow);

    card.appendChild(playersEl);

    // --- Кнопка удаления ---
    const actions = document.createElement('div');
    actions.className = 'admin-ref-actions';

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'admin-ref-btn admin-ref-btn--delete';
    delBtn.textContent = '🗑';
    delBtn.title = t('common.delete');
    delBtn.addEventListener('click', () => {
        void deleteTitle(title);
    });
    actions.appendChild(delBtn);

    card.appendChild(actions);
    return card;
}

async function createTitle(): Promise<void> {
    const name = prompt(t('admin.new_title_prompt'));
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    try {
        const res = await apiRequest<{ item: AdminTitle }>('/api/titles', {
            method: 'POST',
            token: state.token,
            body: { name: trimmed, color: TITLE_COLORS[0]!.value, size: 'small' },
        });
        res.item.player_ids = [];
        titlesCache.unshift(res.item);
        // Добавляем только новую карточку — остальные не трогаем
        if (titlesBox) {
            const emptyHint = titlesBox.querySelector('.hint');
            if (emptyHint) titlesBox.innerHTML = '';
            titlesBox.prepend(buildTitleCard(res.item));
        }
    } catch (err) {
        alert(t('admin.new_title_error') + (err instanceof Error ? err.message : String(err)));
    }
}

/** Отправляет текущие поля титула на сервер (автocохранение). */
async function saveTitle(id: number): Promise<void> {
    const t = getTitle(id);
    if (!t) return;
    const name = t.name.trim();
    if (!name) return; // пустое имя — ждём, пока пользователь введёт

    try {
        const res = await apiRequest<{ item: { name: string; name_en: string | null; color: string; size: TitleSize } }>(
            `/api/titles/${id}`,
            {
                method: 'PATCH',
                token: state.token,
                body: { name, name_en: (t.name_en ?? '').trim() || null, color: t.color, size: t.size ?? 'small' },
            }
        );
        // Синхронизируем кэш с ответом сервера (player_ids сохраняем локально)
        const t2 = getTitle(id);
        if (t2) {
            t2.name = res.item.name;
            t2.name_en = res.item.name_en;
            t2.color = res.item.color;
            t2.size = res.item.size;
        }
        setTitleStatus(id, 'saved');
    } catch (err) {
        setTitleStatus(id, 'error');
        console.error('[admin/titles] save error:', err);
    }
}

async function deleteTitle(title: AdminTitle): Promise<void> {
    if (!confirm(t('admin.delete_title_confirm', { name: title.name }))) return;
    const timer = titleDebounce.get(title.id);
    if (timer) {
        clearTimeout(timer);
        titleDebounce.delete(title.id);
    }
    try {
        await apiRequest(`/api/titles/${title.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        titlesCache = titlesCache.filter((x) => x.id !== title.id);
        titlesBox?.querySelector(`[data-title-id="${title.id}"]`)?.remove();
        if (titlesCache.length === 0 && titlesBox) {
            titlesBox.innerHTML = `<p class="hint">${t('admin.titles_empty')}</p>`;
        }
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

async function assignPlayerToTitle(titleId: number, playerId: number): Promise<void> {
    try {
        await apiRequest(`/api/titles/${titleId}/players`, {
            method: 'POST',
            token: state.token,
            body: { playerId },
        });
        const t = getTitle(titleId);
        if (t && !t.player_ids.includes(playerId)) t.player_ids.push(playerId);
        replaceTitleCard(titleId);
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

async function unassignPlayerFromTitle(titleId: number, playerId: number): Promise<void> {
    try {
        await apiRequest(`/api/titles/${titleId}/players/${playerId}`, {
            method: 'DELETE',
            token: state.token,
        });
        const t = getTitle(titleId);
        if (t) t.player_ids = t.player_ids.filter((x) => x !== playerId);
        replaceTitleCard(titleId);
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

async function linkUser(p: PlayerWithStats): Promise<void> {
    const input = prompt(t('admin.link_user_prompt', { name: p.name }));
    if (input === null) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    // Определяем, email это или username
    const isEmail = trimmed.includes('@');
    const body = isEmail ? { email: trimmed } : { username: trimmed };

    try {
        await apiRequest(`/api/admin/players/${p.id}/link-user`, {
            method: 'POST',
            token: state.token,
            body,
        });
        await loadPlayers();
    } catch (err) {
        alert(t('admin.link_user_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function unlinkUser(p: PlayerWithStats): Promise<void> {
    if (!confirm(t('admin.unlink_user_confirm', { name: p.name }))) return;

    try {
        await apiRequest(`/api/admin/players/${p.id}/unlink-user`, {
            method: 'POST',
            token: state.token,
        });
        await loadPlayers();
    } catch (err) {
        alert(t('common.error') + ': ' + (err instanceof Error ? err.message : String(err)));
    }
}

// ============================================================
// Лог действий
// ============================================================
function populateLogActionOptions(): void {
    if (!logsActionSelect) return;
    while (logsActionSelect.options.length > 1) logsActionSelect.remove(1);
    for (const a of LOG_ACTIONS) {
        const opt = document.createElement('option');
        opt.value = a.value;
        opt.textContent = t(a.key as any);
        logsActionSelect.appendChild(opt);
    }
}

async function loadLogs(showSpinner = false): Promise<void> {
    if (!logsBox) return;
    if (showSpinner) logsBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    const params = new URLSearchParams();
    const q = logsSearchInput?.value.trim() ?? '';
    const action = logsActionSelect?.value ?? '';
    if (q) params.set('q', q);
    if (action) params.set('action', action);
    params.set('limit', '200');

    try {
        const res = await apiRequest<AdminLogsResponse>(
            `/api/admin/logs?${params.toString()}`,
            { token: state.token }
        );
        renderLogs(res.logs);
    } catch (err) {
        logsBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderLogs(logs: AdminLog[]): void {
    if (!logsBox) return;

    if (logs.length === 0) {
        logsBox.innerHTML = `<p class="hint">${t('admin.logs_empty')}</p>`;
        return;
    }

    logsBox.innerHTML = '';
    for (const log of logs) {
        logsBox.appendChild(buildLogRow(log));
    }
}

function buildLogRow(log: AdminLog): HTMLElement {
    const row = document.createElement('div');
    row.className = 'admin-log-row';

    const d = new Date(log.created_at);

    const time = document.createElement('div');
    time.className = 'admin-log-time';
    time.textContent = d.toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
    time.title = d.toLocaleString();

    const actor = document.createElement('div');
    actor.className = 'admin-log-actor';
    actor.textContent = log.actor_username
        || (log.actor_id ? log.actor_id.slice(0, 8) : t('admin.logs_system'));
    if (!log.actor_username && log.actor_id) actor.title = log.actor_id;

    const badge = document.createElement('span');
    const prefix = log.action.split('.')[0];
    badge.className = `admin-log-badge admin-log-badge--${prefix}`;
    badge.textContent = actionLabel(log.action);

    const summary = document.createElement('div');
    summary.className = 'admin-log-summary';
    summary.textContent = log.summary;

    row.append(time, actor, badge, summary);
    return row;
}

function actionLabel(action: string): string {
    const key = LOG_ACTION_LABELS[action];
    return key ? t(key as any) : action;
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) return t('admin.last_seen_just_now');
    const min = Math.floor(diff / 60000);
    if (min < 1) return t('admin.last_seen_just_now');
    if (min < 60) return t('admin.last_seen_minutes', { n: String(min) });
    const h = Math.floor(min / 60);
    if (h < 24) return t('admin.last_seen_hours', { n: String(h) });
    const d = Math.floor(h / 24);
    return t('admin.last_seen_days', { n: String(d) });
}

// ============================================================
// Посещения сайта: агрегаты по дням + лог просмотров
// ============================================================
async function loadVisitStats(showSpinner = false): Promise<void> {
    if (!visitsStatsBox) return;
    if (showSpinner) visitsStatsBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    const days = visitsDaysSelect?.value ?? '30';

    try {
        const stats = await apiRequest<AdminVisitStats>(
            `/api/admin/visits/stats?days=${encodeURIComponent(days)}`,
            { token: state.token }
        );
        renderVisitStats(stats);
    } catch (err) {
        visitsStatsBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

/** Список дат from..to — чтобы на графике были и дни без посещений. */
function daysRange(from: string, to: string): string[] {
    const out: string[] = [];
    const start = new Date(`${from}T00:00:00Z`).getTime();
    const end = new Date(`${to}T00:00:00Z`).getTime();
    for (let ts = start; ts <= end; ts += 86_400_000) {
        out.push(new Date(ts).toISOString().slice(0, 10));
    }
    return out;
}

function renderVisitStats(stats: AdminVisitStats): void {
    if (!visitsStatsBox) return;
    visitsStatsBox.innerHTML = '';

    if (!stats?.from || !stats?.to) {
        visitsStatsBox.innerHTML = `<p class="hint">${t('admin.visits_empty')}</p>`;
        return;
    }

    const summary = document.createElement('div');
    summary.className = 'admin-visits-summary';
    const cards: [string, number][] = [
        [t('admin.visits_views'), stats.summary.views],
        [t('admin.visits_visitors'), stats.summary.visitors],
        [t('admin.visits_registered'), stats.summary.registered],
    ];
    for (const [label, value] of cards) {
        const card = document.createElement('div');
        card.className = 'admin-visits-card';
        const num = document.createElement('div');
        num.className = 'admin-visits-card-value';
        num.textContent = value.toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US');
        const caption = document.createElement('div');
        caption.className = 'admin-visits-card-label';
        caption.textContent = label;
        card.append(num, caption);
        summary.appendChild(card);
    }
    visitsStatsBox.appendChild(summary);

    // График: столбик на день
    const byDay = new Map(stats.daily.map((d) => [d.day, d]));
    const days = daysRange(stats.from, stats.to);
    const max = Math.max(1, ...days.map((d) => byDay.get(d)?.views ?? 0));
    const labelEvery = days.length > 45 ? 7 : days.length > 21 ? 5 : 1;
    const today = stats.to;

    const chart = document.createElement('div');
    chart.className = 'admin-visits-chart';
    days.forEach((day, i) => {
        const info = byDay.get(day);
        const views = info?.views ?? 0;

        const col = document.createElement('div');
        col.className = 'admin-visits-col' + (day === today ? ' is-today' : '');
        col.title = `${day}: ${views} ${t('admin.visits_views').toLowerCase()}, ${info?.visitors ?? 0} ${t('admin.visits_visitors').toLowerCase()}`;

        const bar = document.createElement('div');
        bar.className = 'admin-visits-bar';
        const fill = document.createElement('div');
        fill.className = 'admin-visits-bar-fill';
        fill.style.height = views > 0 ? `${Math.max(4, (views / max) * 100)}%` : '0';
        bar.appendChild(fill);

        const label = document.createElement('div');
        label.className = 'admin-visits-col-label';
        if (i % labelEvery === 0 || i === days.length - 1) {
            label.textContent = `${day.slice(8, 10)}.${day.slice(5, 7)}`;
        }

        col.append(bar, label);
        chart.appendChild(col);
    });
    visitsStatsBox.appendChild(chart);

    // Топ страниц
    const topTitle = document.createElement('h3');
    topTitle.className = 'admin-visits-block-title';
    topTitle.textContent = t('admin.visits_top');
    visitsStatsBox.appendChild(topTitle);

    const top = document.createElement('div');
    top.className = 'admin-visits-top';
    if (stats.top_pages.length === 0) {
        top.innerHTML = `<p class="hint">${t('admin.visits_empty')}</p>`;
    }
    for (const page of stats.top_pages) {
        const row = document.createElement('div');
        row.className = 'admin-visits-top-row';
        const path = document.createElement('span');
        path.className = 'admin-visits-top-path';
        path.textContent = page.path;
        const count = document.createElement('span');
        count.className = 'admin-visits-top-count';
        count.textContent = `${page.views} · ${page.visitors}`;
        count.title = `${t('admin.visits_views')}: ${page.views} · ${t('admin.visits_visitors')}: ${page.visitors}`;
        row.append(path, count);
        top.appendChild(row);
    }
    visitsStatsBox.appendChild(top);
}

async function loadVisits(showSpinner = false): Promise<void> {
    if (!visitsLogBox) return;
    if (showSpinner) visitsLogBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    const params = new URLSearchParams();
    const q = visitsSearchInput?.value.trim() ?? '';
    if (q) params.set('q', q);
    params.set('limit', '200');

    try {
        const res = await apiRequest<AdminVisitsResponse>(
            `/api/admin/visits?${params.toString()}`,
            { token: state.token }
        );
        renderVisits(res.visits);
    } catch (err) {
        visitsLogBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderVisits(visits: AdminVisit[]): void {
    if (!visitsLogBox) return;

    if (visits.length === 0) {
        visitsLogBox.innerHTML = `<p class="hint">${t('admin.visits_empty')}</p>`;
        return;
    }

    visitsLogBox.innerHTML = '';
    for (const visit of visits) {
        visitsLogBox.appendChild(buildVisitRow(visit));
    }
}

function buildVisitRow(visit: AdminVisit): HTMLElement {
    const row = document.createElement('div');
    row.className = 'admin-visit-row';

    const d = new Date(visit.created_at);
    const time = document.createElement('div');
    time.className = 'admin-log-time';
    time.textContent = d.toLocaleString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
    time.title = d.toLocaleString();

    const path = document.createElement('div');
    path.className = 'admin-visit-path';
    path.textContent = visit.path;
    path.title = visit.path;

    const visitor = document.createElement('div');
    visitor.className = 'admin-visit-visitor';
    if (visit.username) {
        visitor.textContent = visit.username;
        visitor.title = visit.user_id ?? '';
    } else {
        visitor.textContent = t('admin.visits_anonymous');
        visitor.classList.add('is-anon');
        visitor.title = visit.visitor_id ?? visit.ip ?? '';
    }

    const referrer = document.createElement('div');
    referrer.className = 'admin-visit-referrer';
    referrer.textContent = referrerLabel(visit.referrer);
    if (visit.referrer) referrer.title = visit.referrer;

    const ua = document.createElement('div');
    ua.className = 'admin-visit-ua';
    ua.textContent = shortUserAgent(visit.user_agent);
    if (visit.user_agent) ua.title = visit.user_agent;

    row.append(time, path, visitor, referrer, ua);
    return row;
}

function referrerLabel(referrer: string | null): string {
    if (!referrer) return t('admin.visits_direct');
    try {
        return new URL(referrer).host || referrer;
    } catch {
        return referrer.slice(0, 60);
    }
}

function shortUserAgent(ua: string | null): string {
    if (!ua) return '—';
    const browser = /Edg\//.test(ua) ? 'Edge'
        : /OPR\/|Opera/.test(ua) ? 'Opera'
            : /Firefox\//.test(ua) ? 'Firefox'
                : /Chrome\//.test(ua) ? 'Chrome'
                    : /Safari\//.test(ua) ? 'Safari'
                        : '—';
    const os = /Windows/.test(ua) ? 'Windows'
        : /Android/.test(ua) ? 'Android'
            : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
                : /Mac OS X/.test(ua) ? 'macOS'
                    : /Linux/.test(ua) ? 'Linux'
                        : '';
    return os ? `${browser} · ${os}` : browser;
}

// ============================================================
// Стримеры (Twitch-каналы). Правки — как у карт: дебаунс-автосохранение
// без кнопки «Сохранить» и без перезагрузки списка. Live-статус сайт
// определяет сам через Twitch API (плашка «Сейчас в эфире»).
// ============================================================
const streamersCache = new Map<number, AdminStreamer>();
const streamerDebounce = new Map<number, ReturnType<typeof setTimeout>>();
const STREAMER_AUTOSAVE_DELAY = 700;

async function loadStreamers(): Promise<void> {
    if (!streamersBox) return;
    streamersBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const res = await apiRequest<{ streamers: AdminStreamer[] }>(
            '/api/admin/streamers',
            { token: state.token }
        );
        streamersCache.clear();
        for (const s of res.streamers) streamersCache.set(s.id, s);
        renderStreamers(res.streamers);
    } catch (err) {
        streamersBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderStreamers(items: AdminStreamer[]): void {
    if (!streamersBox) return;

    if (items.length === 0) {
        streamersBox.innerHTML = `<p class="hint">${t('admin.streamers_empty')}</p>`;
        return;
    }

    streamersBox.innerHTML = '';
    for (const s of items) streamersBox.appendChild(buildStreamerCard(s));
}

function setStreamerStatus(id: number, status: 'idle' | 'saving' | 'saved' | 'error'): void {
    const card = document.querySelector<HTMLElement>(
        `.admin-ref-card--streamer[data-ref-id="${id}"]`
    );
    const el = card?.querySelector('.ref-save-status') as HTMLElement | null;
    if (!el) return;
    el.classList.remove('is-saving', 'is-saved', 'is-error');
    if (status === 'saving') {
        el.classList.add('is-saving');
        el.textContent = '…';
        el.title = t('common.saving');
    } else if (status === 'saved') {
        el.classList.add('is-saved');
        el.textContent = '✓';
        el.title = t('common.saved');
    } else if (status === 'error') {
        el.classList.add('is-error');
        el.textContent = '✗';
        el.title = t('common.error');
    } else {
        el.textContent = '';
        el.title = '';
    }
}

/** Правит карточку в кэше и ставит отложенное сохранение. */
function patchStreamer(item: AdminStreamer, patch: Partial<AdminStreamer>): void {
    Object.assign(item, patch);
    streamersCache.set(item.id, item);
    const prev = streamerDebounce.get(item.id);
    if (prev) clearTimeout(prev);
    streamerDebounce.set(item.id, setTimeout(() => void saveStreamer(item.id), STREAMER_AUTOSAVE_DELAY));
    setStreamerStatus(item.id, 'saving');
}

/** Сохраняет стримера на сервере, не перерисовывая список. */
async function saveStreamer(id: number): Promise<void> {
    const item = streamersCache.get(id);
    if (!item) return;

    // Пустые имя/URL не отправляем (ждём, пока пользователь допишет),
    // но test_live сохраняем всегда — переключать его можно сразу.
    const body: Record<string, unknown> = {
        test_live: item.test_live === true,
        // null — отвязать игрока (поле очистили).
        player_id: item.player_id ?? null,
    };
    const name = item.name.trim();
    const url = item.url.trim();
    if (name) body.name = name;
    if (url) body.url = url;

    try {
        const res = await apiRequest<{ streamer: AdminStreamer }>(
            `/api/admin/streamers/${id}`,
            { method: 'PATCH', token: state.token, body }
        );
        Object.assign(item, res.streamer);
        setStreamerStatus(id, 'saved');
    } catch (err) {
        setStreamerStatus(id, 'error');
        console.error('[admin/streamers] autosave error:', err);
    }
}

function buildStreamerCard(item: AdminStreamer): HTMLElement {
    const card = document.createElement('div');
    card.className = 'admin-ref-card admin-ref-card--streamer';
    card.dataset.refId = String(item.id);

    const status = document.createElement('span');
    status.className = 'ref-save-status';
    card.appendChild(status);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'admin-ref-input';
    nameInput.placeholder = t('admin.streamer_name_placeholder');
    nameInput.value = item.name;
    nameInput.addEventListener('input', () => patchStreamer(item, { name: nameInput.value }));
    card.appendChild(nameInput);

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.className = 'admin-ref-input';
    urlInput.placeholder = t('admin.streamer_url_placeholder');
    urlInput.value = item.url;
    urlInput.addEventListener('input', () => patchStreamer(item, { url: urlInput.value }));
    card.appendChild(urlInput);

    // Привязка к профилю игрока: у него в карточке появится раздел «Ссылки».
    const playerInput = document.createElement('input');
    playerInput.type = 'text';
    playerInput.className = 'admin-ref-input';
    playerInput.placeholder = t('admin.streamer_player_placeholder');
    playerInput.title = t('admin.streamer_player_hint');
    playerInput.value = item.player_name ?? '';
    playerInput.addEventListener('input', () => {
        const name = playerInput.value.trim();
        if (!name) {
            playerInput.classList.remove('is-invalid');
            patchStreamer(item, { player_id: null, player_name: null });
            return;
        }
        const player = findPlayerByName(name);
        if (!player) {
            // Не сохраняем: иначе случайный текст отвязал бы игрока.
            playerInput.classList.add('is-invalid');
            return;
        }
        playerInput.classList.remove('is-invalid');
        patchStreamer(item, { player_id: player.id, player_name: player.name });
    });
    card.appendChild(playerInput);
    attachAutocomplete(playerInput, () => cachedPlayers.map((p) => p.name));

    // «Тест»: принудительно показывает стрим в плашке (для проверки визуала).
    const testLabel = document.createElement('label');
    testLabel.className = 'admin-ref-type admin-streamer-test';
    testLabel.title = t('admin.streamer_test_title');
    const testCb = document.createElement('input');
    testCb.type = 'checkbox';
    testCb.checked = item.test_live === true;
    // Чекбокс сохраняем сразу (без дебаунса): иначе быстрый перезагруз потерял бы переключение.
    testCb.addEventListener('change', () => {
        item.test_live = testCb.checked;
        streamersCache.set(item.id, item);
        setStreamerStatus(item.id, 'saving');
        void saveStreamer(item.id);
    });
    const testSpan = document.createElement('span');
    testSpan.textContent = t('admin.streamer_test');
    testLabel.append(testCb, testSpan);
    card.appendChild(testLabel);

    const actions = document.createElement('div');
    actions.className = 'admin-ref-actions';

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'admin-ref-btn admin-ref-btn--delete';
    delBtn.textContent = '🗑';
    delBtn.title = t('common.delete');
    delBtn.addEventListener('click', () => void deleteStreamer(item));
    actions.appendChild(delBtn);

    card.appendChild(actions);
    return card;
}

async function addStreamer(): Promise<void> {
    const url = prompt(t('admin.new_streamer_prompt'));
    if (url === null) return;
    const trimmed = url.trim();
    if (!trimmed) return;

    const name = prompt(t('admin.new_streamer_name_prompt'), guessStreamerLogin(trimmed) ?? '');
    if (name === null) return;
    const finalName = name.trim();
    if (!finalName) {
        alert(t('admin.streamer_name_required'));
        return;
    }

    try {
        await apiRequest('/api/admin/streamers', {
            method: 'POST',
            token: state.token,
            body: { name: finalName, url: trimmed },
        });
        await loadStreamers();
    } catch (err) {
        alert(t('admin.streamer_save_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function deleteStreamer(item: AdminStreamer): Promise<void> {
    if (!confirm(t('admin.delete_streamer_confirm', { name: item.name }))) return;
    try {
        await apiRequest(`/api/admin/streamers/${item.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        await loadStreamers();
    } catch (err) {
        alert(t('admin.streamer_delete_error') + (err instanceof Error ? err.message : String(err)));
    }
}

/** Ищет игрока по имени (для привязки стримера к профилю). */
function findPlayerByName(raw: string): PlayerWithStats | null {
    const needle = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!needle) return null;
    return (
        cachedPlayers.find(
            (p) => p.name.trim().toLowerCase().replace(/\s+/g, ' ') === needle
        ) ?? null
    );
}

/** Пытается достать логин канала из ссылки (для имени по умолчанию). */
function guessStreamerLogin(raw: string): string | null {
    try {
        const u = new URL(raw);
        const seg = u.pathname.split('/').filter(Boolean);
        return seg[0] || null;
    } catch {
        return null;
    }
}

// ============================================================
// Донаты (полученные переводы). Ведёт админ, показываются списком внизу
// страницы «Поддержать проект». Правка карточек — дебаунс-автосохранение
// (как у карт/стримеров), добавление — из формы в шапке секции.
// ============================================================
const donationsCache = new Map<number, AdminDonation>();
const donationDebounce = new Map<number, ReturnType<typeof setTimeout>>();
const DONATION_AUTOSAVE_DELAY = 700;
const DONATION_CURRENCIES = ['RUB', 'USD', 'EUR'] as const;

async function loadDonations(): Promise<void> {
    if (!donationsBox) return;
    donationsBox.innerHTML = '<div class="skeleton skeleton-block"></div>';

    try {
        const res = await apiRequest<{ donations: AdminDonation[] }>(
            '/api/admin/donations',
            { token: state.token }
        );
        donationsCache.clear();
        for (const d of res.donations) donationsCache.set(d.id, d);
        renderDonations(res.donations);
    } catch (err) {
        donationsBox.innerHTML = `<p class="error">${t('common.error')}: ${err instanceof Error ? err.message : String(err)
            }</p>`;
    }
}

function renderDonations(items: AdminDonation[]): void {
    if (!donationsBox) return;

    if (items.length === 0) {
        donationsBox.innerHTML = `<p class="hint">${t('admin.donations_empty')}</p>`;
        return;
    }

    donationsBox.innerHTML = '';
    for (const d of items) donationsBox.appendChild(buildDonationCard(d));
}

function setDonationStatus(id: number, status: 'idle' | 'saving' | 'saved' | 'error'): void {
    const card = document.querySelector<HTMLElement>(
        `.admin-ref-card--donation[data-ref-id="${id}"]`
    );
    const el = card?.querySelector('.ref-save-status') as HTMLElement | null;
    if (!el) return;
    el.classList.remove('is-saving', 'is-saved', 'is-error');
    if (status === 'saving') {
        el.classList.add('is-saving');
        el.textContent = '…';
        el.title = t('common.saving');
    } else if (status === 'saved') {
        el.classList.add('is-saved');
        el.textContent = '✓';
        el.title = t('common.saved');
    } else if (status === 'error') {
        el.classList.add('is-error');
        el.textContent = '✗';
        el.title = t('common.error');
    } else {
        el.textContent = '';
        el.title = '';
    }
}

/** Правит донат в кэше и ставит отложенное сохранение. */
function patchDonation(item: AdminDonation, patch: Partial<AdminDonation>): void {
    Object.assign(item, patch);
    donationsCache.set(item.id, item);
    const prev = donationDebounce.get(item.id);
    if (prev) clearTimeout(prev);
    donationDebounce.set(item.id, setTimeout(() => void saveDonation(item.id), DONATION_AUTOSAVE_DELAY));
    setDonationStatus(item.id, 'saving');
}

/** Сохраняет донат на сервере, не перерисовывая список. */
async function saveDonation(id: number): Promise<void> {
    const item = donationsCache.get(id);
    if (!item) return;
    const donor_name = item.donor_name.trim();
    const amount = Number(item.amount);
    if (!donor_name || !Number.isFinite(amount) || amount <= 0) return; // ждём корректных данных
    try {
        const res = await apiRequest<{ donation: AdminDonation }>(
            `/api/admin/donations/${id}`,
            {
                method: 'PATCH',
                token: state.token,
                body: {
                    donor_name,
                    amount,
                    currency: item.currency,
                    message: item.message,
                    donated_at: item.donated_at,
                },
            }
        );
        Object.assign(item, res.donation);
        setDonationStatus(id, 'saved');
    } catch (err) {
        setDonationStatus(id, 'error');
        console.error('[admin/donations] autosave error:', err);
    }
}

function buildDonationCard(item: AdminDonation): HTMLElement {
    const card = document.createElement('div');
    card.className = 'admin-ref-card admin-ref-card--donation';
    card.dataset.refId = String(item.id);

    const status = document.createElement('span');
    status.className = 'ref-save-status';
    card.appendChild(status);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'admin-ref-input';
    nameInput.placeholder = t('admin.donation_name_placeholder');
    nameInput.value = item.donor_name;
    nameInput.addEventListener('input', () => patchDonation(item, { donor_name: nameInput.value }));
    card.appendChild(nameInput);

    const amountInput = document.createElement('input');
    amountInput.type = 'number';
    amountInput.min = '0';
    amountInput.step = '0.01';
    amountInput.className = 'admin-ref-input admin-ref-input--small';
    amountInput.placeholder = t('admin.donation_amount_placeholder');
    amountInput.value = String(item.amount);
    amountInput.addEventListener('input', () => patchDonation(item, { amount: Number(amountInput.value) }));
    card.appendChild(amountInput);

    const currencySelect = document.createElement('select');
    currencySelect.className = 'admin-ref-input admin-ref-input--small';
    for (const cur of DONATION_CURRENCIES) {
        const opt = document.createElement('option');
        opt.value = cur;
        opt.textContent = cur;
        if (cur === item.currency) opt.selected = true;
        currencySelect.appendChild(opt);
    }
    currencySelect.addEventListener('change', () =>
        patchDonation(item, { currency: currencySelect.value }));
    card.appendChild(currencySelect);

    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.className = 'admin-ref-input admin-ref-input--small';
    dateInput.value = item.donated_at;
    dateInput.addEventListener('change', () => patchDonation(item, { donated_at: dateInput.value }));
    card.appendChild(dateInput);

    const messageInput = document.createElement('input');
    messageInput.type = 'text';
    messageInput.className = 'admin-ref-input admin-donation-message';
    messageInput.placeholder = t('admin.donation_message_placeholder');
    messageInput.value = item.message ?? '';
    messageInput.addEventListener('input', () =>
        patchDonation(item, { message: messageInput.value.trim() || null }));
    card.appendChild(messageInput);

    const actions = document.createElement('div');
    actions.className = 'admin-ref-actions';

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'admin-ref-btn admin-ref-btn--delete';
    delBtn.textContent = '🗑';
    delBtn.title = t('common.delete');
    delBtn.addEventListener('click', () => void deleteDonation(item));
    actions.appendChild(delBtn);

    card.appendChild(actions);
    return card;
}

async function addDonation(): Promise<void> {
    const nameInput = document.getElementById('donation-name') as HTMLInputElement | null;
    const amountInput = document.getElementById('donation-amount') as HTMLInputElement | null;
    const currencySelect = document.getElementById('donation-currency') as HTMLSelectElement | null;
    const dateInput = document.getElementById('donation-date') as HTMLInputElement | null;
    const messageInput = document.getElementById('donation-message') as HTMLInputElement | null;

    const donor_name = nameInput?.value.trim() ?? '';
    const amount = Number(amountInput?.value);
    if (!donor_name || !Number.isFinite(amount) || amount <= 0) {
        alert(t('admin.donation_required'));
        return;
    }

    try {
        await apiRequest('/api/admin/donations', {
            method: 'POST',
            token: state.token,
            body: {
                donor_name,
                amount,
                currency: currencySelect?.value ?? 'RUB',
                message: messageInput?.value.trim() || null,
                donated_at: dateInput?.value || undefined,
            },
        });
        if (nameInput) nameInput.value = '';
        if (amountInput) amountInput.value = '';
        if (messageInput) messageInput.value = '';
        await loadDonations();
    } catch (err) {
        alert(t('admin.donation_save_error') + (err instanceof Error ? err.message : String(err)));
    }
}

async function deleteDonation(item: AdminDonation): Promise<void> {
    if (!confirm(t('admin.delete_donation_confirm', { name: item.donor_name }))) return;
    try {
        await apiRequest(`/api/admin/donations/${item.id}`, {
            method: 'DELETE',
            token: state.token,
        });
        await loadDonations();
    } catch (err) {
        alert(t('admin.donation_delete_error') + (err instanceof Error ? err.message : String(err)));
    }
}